<template>
  <v-container fluid class="px-lg-5">
    <app-header
      :title="pageTitle"
      :breadcrumbs="breadcrumbs"
      :showButton="false"
    />

    <template v-if="appraisal && loaded">
      <v-alert v-if="isCanceled" type="error" outlined class="mb-4">
        {{ $t('views.appraisals.show.cancelledNotice') }}
      </v-alert>

      <answer-set v-if="isPendingEmployee && isEmployeeViewer" ref="answerSet"
        :questions="appraisal.questions" v-model="draftAnswers" :readonly="false" />

      <answer-set v-else-if="isPendingEmployee"
        :questions="appraisal.questions" :value="flattenAnswers('employee')" readonly />

      <manager-evaluation v-else-if="isPendingSupervisor && isSupervisorViewer" ref="managerEvaluation"
        :questions="appraisal.questions" :employee-answers="flattenAnswers('employee')" v-model="draftManagerAnswers" />

      <answer-set v-else-if="isPendingSupervisor"
        :questions="appraisal.questions" :value="flattenAnswers('employee')" readonly />

      <final-answer v-else-if="isReadyForMeeting && (isEmployeeViewer || isSupervisorViewer)" ref="finalAnswer"
        :questions="appraisal.questions" :employee-answers="flattenAnswers('employee')"
        :supervisor-answers="flattenAnswers('supervisor')" v-model="draftFinalAnswers" />

      <template v-else-if="isReadyForMeeting">
        <v-card v-for="(question, index) in appraisal.questions" :key="question.id" class="rounded-card mb-4" flat
          outlined>
          <v-card-text>
            <div class="overline grey--text text--darken-1 mb-1">
              {{ $t('views.appraisals.show.questionLabel', { number: index + 1 }) }}
            </div>
            <div class="text-h6 font-weight-medium mb-3">
              {{ question.body }}
              <span v-if="question.isScored" class="body-2 grey--text text--darken-1 font-weight-regular">
                ({{ $t('models.appraisal.attributes.weightValue', { weight: question.weight }) }})
              </span>
            </div>

            <div class="caption grey--text text--darken-1 mb-1">
              {{ $t('views.appraisals.show.employeeResponse') }}
            </div>
            <app-field type="text" :label="$t('models.appraisal.attributes.answer')"
              :value="formatAnswer(question, flattenAnswers('employee')[question.id])" disabled hide-details />

            <div class="caption grey--text text--darken-1 mt-4 mb-1">
              {{ $t('views.appraisals.show.managerAnswers') }}
            </div>
            <app-field type="text" :label="$t('models.appraisal.attributes.answer')"
              :value="formatAnswer(question, flattenAnswers('supervisor')[question.id])" disabled hide-details />
          </v-card-text>
        </v-card>
      </template>

      <answer-set v-else-if="isCompleted"
        :questions="appraisal.questions" :value="flattenAnswers('meeting')" readonly show-scores
        :title="$t('views.appraisals.show.finalAnswers')" />

      <answer-set v-else-if="isCanceled"
        :questions="appraisal.questions" :value="flattenAnswers('employee')" readonly />

      <v-row no-gutters>
        <v-col class="col-12 text-right">
          <template v-if="activeStage">
            <v-btn class="primary mr-2" @click="handleSubmit">{{ $t('actions.submit') }}</v-btn>
            <v-btn class="secondary mr-2" @click="handleSave">{{ $t('actions.saveAndContinueLater') }}</v-btn>
            <v-btn text @click="handleCancel">{{ $t('actions.cancel') }}</v-btn>
          </template>
          <v-btn v-else text @click="goToList">{{ $t('actions.back') }}</v-btn>
        </v-col>
      </v-row>
    </template>
  </v-container>
</template>

<script>
import {mapActions, mapGetters} from 'vuex';
import {appraisalStatuses, appraisalTemplateQuestionTypes} from 'constants';
import AnswerSet from 'components/appraisals/AnswerSet';
import ManagerEvaluation from 'components/appraisals/ManagerEvaluation';
import FinalAnswer from 'components/appraisals/FinalAnswer';
import {formatAnswer} from '../../../helpers/appraisalAnswers';

export default {
  name: 'Appraisal',
  components: {
    AnswerSet, ManagerEvaluation, FinalAnswer
  },
  data() {
    return {
      questionTypes: appraisalTemplateQuestionTypes,
      draftAnswers: {},
      draftManagerAnswers: {},
      draftFinalAnswers: {},
      loaded: false
    };
  },
  computed: {
    ...mapGetters(['appraisal', 'currentUser']),
    pageTitle() {
      return {
        text: this.appraisal ? this.appraisal.title : this.$tc('models.appraisal.entity', 1),
        icon: 'mdi-clipboard-text-outline'
      };
    },
    isEmployeeViewer() {
      return !!this.appraisal && `${this.appraisal.employeeId}` === `${this.currentUser.id}`;
    },
    isSupervisorViewer() {
      return !!this.appraisal && `${this.appraisal.supervisorId}` === `${this.currentUser.id}`;
    },
    activeStage() {
      if (this.isPendingEmployee && this.isEmployeeViewer) {
        return 'employee';
      }
      if (this.isPendingSupervisor && this.isSupervisorViewer) {
        return 'supervisor';
      }
      if (this.isReadyForMeeting && (this.isEmployeeViewer || this.isSupervisorViewer)) {
        return 'meeting';
      }
      return null;
    },
    breadcrumbs() {
      const listCrumb = this.isSupervisorViewer ?
        {
          text: this.$t('layout.mainMenu.manageAppraisals'), to: {name: 'ManageAppraisals'}, exact: true
        } :
        {
          text: this.$t('layout.accountNavMenu.appraisals'), to: {name: 'UserAppraisals', params: {uuid: 'me'}}, exact: true
        };
      return [
        {text: this.$t('layout.mainMenu.home'), to: {name: 'Home'}},
        listCrumb,
        {
          text: this.$tc('models.appraisal.entity', 1), to: {name: 'Appraisal', params: {id: this.$route.params.id}}, exact: true
        }
      ];
    },
    isPendingEmployee() {
      return this.appraisal.status === appraisalStatuses.PENDING_EMPLOYEE;
    },
    isPendingSupervisor() {
      return this.appraisal.status === appraisalStatuses.PENDING_SUPERVISOR;
    },
    isReadyForMeeting() {
      return this.appraisal.status === appraisalStatuses.READY_FOR_MEETING;
    },
    isCompleted() {
      return this.appraisal.status === appraisalStatuses.COMPLETED;
    },
    isCanceled() {
      return this.appraisal.status === appraisalStatuses.CANCELED;
    }
  },
  methods: {
    ...mapActions([
      'getAppraisal', 'saveAppraisalAnswers', 'submitEmployeeAnswers', 'submitSupervisorAnswers', 'submitMeetingAnswers'
    ]),
    formatAnswer,
    flattenAnswers(stage) {
      return this.appraisal.questions.reduce((answers, question) => {
        const answer = question.answers[stage];
        const isMcq = question.questionType === this.questionTypes.MCQ;
        answers[question.id] = answer ? (isMcq ? answer.appraisal_template_question_option_id : answer.body) : '';
        return answers;
      }, {});
    },
    draftFor(stage) {
      return {
        employee: this.draftAnswers, supervisor: this.draftManagerAnswers, meeting: this.draftFinalAnswers
      }[stage];
    },
    refFor(stage) {
      return {
        employee: this.$refs.answerSet, supervisor: this.$refs.managerEvaluation, meeting: this.$refs.finalAnswer
      }[stage];
    },
    submitActionFor(stage) {
      return {
        employee: this.submitEmployeeAnswers,
        supervisor: this.submitSupervisorAnswers,
        meeting: this.submitMeetingAnswers
      }[stage];
    },
    async fetchAppraisal() {
      await this.getAppraisal(this.$route.params.id);
      if (this.appraisal) {
        this.draftAnswers = this.flattenAnswers('employee');
        this.draftManagerAnswers = this.flattenAnswers('supervisor');
        this.draftFinalAnswers = this.flattenAnswers('meeting');
      }
      this.loaded = true;
    },
    async confirm(message) {
      return this.$confirm(message, {
        buttonTrueText: this.$t('actions.confirm'),
        buttonFalseText: this.$t('actions.cancel')
      });
    },
    async handleSave() {
      const response = await this.saveAppraisalAnswers({
        id: this.appraisal.id, questions: this.appraisal.questions, answers: this.draftFor(this.activeStage)
      });
      if (this.successCode(response.status)) {
        this.$snotify.success(this.$t('messages.success.updated', {entity: this.$tc('models.appraisal.entity', 1)}));
        this.goToList();
      }
    },
    async handleSubmit() {
      if (!this.refFor(this.activeStage).validate()) {
        return;
      }
      if (!await this.confirm(this.$t('messages.confirmation.submitAppraisal'))) {
        return;
      }
      const response = await this.submitActionFor(this.activeStage)({
        id: this.appraisal.id, questions: this.appraisal.questions, answers: this.draftFor(this.activeStage)
      });
      if (this.successCode(response.status)) {
        this.goToList();
      }
    },
    async handleCancel() {
      const hasUnsavedChanges = JSON.stringify(this.draftFor(this.activeStage)) !==
        JSON.stringify(this.flattenAnswers(this.activeStage));
      if (hasUnsavedChanges && !await this.confirm(this.$t('messages.confirmation.discardUnsavedChanges'))) {
        return;
      }
      this.goToList();
    },
    goToList() {
      if (this.isSupervisorViewer) {
        this.$router.push({name: 'ManageAppraisals'});
      } else {
        this.$router.push({name: 'UserAppraisals', params: {uuid: 'me'}});
      }
    }
  },
  created() {
    this.fetchAppraisal();
  }
};
</script>
